import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { FormsModule } from '@angular/forms';
import { XeroReauthorizeModalComponent } from './xero-reauthorize-modal/xero-reauthorize-modal.component';

@Component({
  selector: 'app-admin-utilities',
  templateUrl: './admin-utilities.component.html',
  styleUrls: ['./admin-utilities.component.css'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatSelectModule, MatFormFieldModule, MatExpansionModule, MatTableModule, MatSortModule, MatCheckboxModule, MatDialogModule, FormsModule]
})
export class AdminUtilitiesComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('alertsSort', { read: MatSort }) alertsSort!: MatSort;
  @ViewChild('invoicesSort', { read: MatSort }) invoicesSort!: MatSort;

  userId = sessionStorage.getItem('UserId');

  isProcessing = false;
  processingTask = '';

  // Email batch progress
  emailSessionId: string | null = null;
  emailTotal = 0;
  emailSent = 0;
  emailFailed = 0;
  emailRemaining = 0;
  private pollInterval: ReturnType<typeof setInterval> | null = null;

  // Invoice batch progress
  invoiceSessionId: string | null = null;
  invoiceTotal = 0;
  invoiceSent = 0;
  invoiceSkipped = 0;
  invoiceRemaining = 0;
  invoiceCurrentItem: string | null = null;
  private invoicePollInterval: ReturnType<typeof setInterval> | null = null;
  
  // Invoice selection
  selectedInvoiceIds: number[] = [];
  overrideInvoiceDate = false;
  invoiceDateOverride = '';

  // Preview data
  alertsDataSource = new MatTableDataSource<any>([]);
  invoicesDataSource = new MatTableDataSource<any>([]);
  showAlertsPreview = false;
  showInvoicesPreview = false;
  loadingAlertsPreview = false;
  loadingInvoicesPreview = false;

  // Table column definitions
  alertsColumns = ['clientName', 'subscriptionName', 'contactName', 'email', 'notificationCountInAlert'];
  invoicesColumns = ['select', 'name', 'serviceTypeName', 'startDate', 'invoiceFrequency', 'lastInvoiceDate', 'daysSinceLastInvoice', 'notificationCount', 'paymentCancelledNotificationCount'];

  // Test mode settings
  alertsTestMode = false;
  invoicesTestMode = false;
  testEmailSendToAddress: string | null = null;
  testFinanceContactId: string | null = null;

  constructor(private http: HttpClient, private snackBar: MatSnackBar, private dialog: MatDialog) {
    this.loadTestModeSettings();
  }

  ngOnInit(): void {}

  ngAfterViewChecked(): void {
    if (this.alertsSort && !this.alertsDataSource.sort) {
      this.alertsDataSource.sort = this.alertsSort;
    }
    if (this.invoicesSort && !this.invoicesDataSource.sort) {
      this.invoicesDataSource.sort = this.invoicesSort;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopInvoicePolling();
  }

  private loadTestModeSettings(): void {
    this.http.get<any>('charitycentral/GetTestModeSettings').subscribe({
      next: (settings) => {
        this.alertsTestMode = settings.alertsTestMode;
        this.invoicesTestMode = settings.invoicesTestMode;
        this.testEmailSendToAddress = settings.testEmailSendToAddress ?? null;
        this.testFinanceContactId = settings.testFinanceContactId ?? null;
      },
      error: (error) => {
        console.error('Error loading test mode settings:', error);
      }
    });
  }

  runNotifications(): void {
    this.isProcessing = true;
    this.processingTask = 'Starting Alerts';
    this.emailSessionId = null;
    this.emailTotal = this.alertsDataSource.data.length; // use preview count as initial estimate
    this.emailSent = 0;
    this.emailFailed = 0;
    this.emailRemaining = this.emailTotal;

    this.http.get<{ sessionId: string }>(`charitycentral/RunScheduledAlertBatch?UserId=${this.userId}`).subscribe({
      next: (response) => {
        this.emailSessionId = response.sessionId;
        this.processingTask = 'Sending Alerts';
        this.startPolling();
      },
      error: (error) => {
        this.isProcessing = false;
        this.snackBar.open(`Error starting alerts: ${error.message}`, 'Close', { duration: 5000 });
      }
    });
  }

  private startPolling(): void {
    this.pollInterval = setInterval(() => {
      if (!this.emailSessionId) return;
      this.http.get<any>(`charitycentral/GetAlertBatchProgress?sessionId=${this.emailSessionId}`).subscribe({
        next: (progress) => {
          if (progress.total > 0) this.emailTotal = progress.total;
          this.emailSent = progress.sent;
          this.emailFailed = progress.failed;
          this.emailRemaining = progress.remaining;

          if (progress.isComplete) {
            this.stopPolling();
            this.isProcessing = false;
            if (progress.errorMessage) {
              this.snackBar.open(`Alerts failed: ${progress.errorMessage}`, 'Close', { duration: 10000 });
            } else {
              const msg = progress.failed > 0
                ? `Alerts completed: ${progress.sent} sent, ${progress.failed} failed`
                : `Alerts completed: ${progress.sent} emails sent successfully`;
              this.snackBar.open(msg, 'Close', { duration: 8000 });
            }
          }
        },
        error: () => { /* ignore transient polling errors */ }
      });
    }, 3000);
  }

  private stopPolling(): void {
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  sendSelectedInvoices(): void {
    if (this.selectedInvoiceIds.length === 0) {
      this.snackBar.open('Please select at least one invoice to send', 'Close', { duration: 5000 });
      return;
    }

    this.isProcessing = true;
    this.processingTask = 'Starting Invoices';
    this.invoiceSessionId = null;
    this.invoiceTotal = this.selectedInvoiceIds.length;
    this.invoiceSent = 0;
    this.invoiceSkipped = 0;
    this.invoiceRemaining = this.invoiceTotal;
    this.invoiceCurrentItem = null;

    let runUrl = `charitycentral/RunSelectedInvoices?UserId=${this.userId}`;
    if (this.overrideInvoiceDate && this.invoiceDateOverride) {
      runUrl += `&invoiceDate=${encodeURIComponent(this.invoiceDateOverride)}`;
    }

    this.http.post<{ sessionId: string }>(runUrl, this.selectedInvoiceIds).subscribe({
      next: (response) => {
        this.invoiceSessionId = response.sessionId;
        this.processingTask = 'Sending Invoices';
        this.startInvoicePolling();
      },
      error: (error) => {
        this.isProcessing = false;
        this.snackBar.open(`Error starting invoices: ${error.error?.message || error.message}`, 'Close', { duration: 5000 });
      }
    });
  }

  toggleInvoiceSelection(subscriptionId: number): void {
    const idx = this.selectedInvoiceIds.indexOf(subscriptionId);
    if (idx === -1) {
      this.selectedInvoiceIds = [...this.selectedInvoiceIds, subscriptionId];
    } else {
      this.selectedInvoiceIds = this.selectedInvoiceIds.filter(id => id !== subscriptionId);
    }
  }

  isInvoiceSelected(subscriptionId: number): boolean {
    return this.selectedInvoiceIds.includes(subscriptionId);
  }

  isAllInvoicesSelected(): boolean {
    return this.invoicesDataSource.data.length > 0 &&
      this.invoicesDataSource.data.every(row => this.selectedInvoiceIds.includes(row.subscriptionId));
  }

  masterToggleInvoices(): void {
    if (this.isAllInvoicesSelected()) {
      this.selectedInvoiceIds = [];
    } else {
      this.selectedInvoiceIds = this.invoicesDataSource.data.map(row => row.subscriptionId);
    }
  }

  formatFrequency(code: string): string {
    switch (code) {
      case 'M': return 'Monthly';
      case 'Q': return 'Quarterly';
      case 'A': return 'Annual';
      default: return code ?? '-';
    }
  }

  private startInvoicePolling(): void {
    this.invoicePollInterval = setInterval(() => {
      if (!this.invoiceSessionId) return;
      this.http.get<any>(`charitycentral/GetInvoiceBatchProgress?sessionId=${this.invoiceSessionId}`).subscribe({
        next: (progress) => {
          if (progress.total > 0) this.invoiceTotal = progress.total;
          this.invoiceSent = progress.sent;
          this.invoiceSkipped = progress.skipped;
          this.invoiceRemaining = progress.remaining;
          this.invoiceCurrentItem = progress.currentItem ?? null;

          if (progress.isComplete) {
            this.stopInvoicePolling();
            this.isProcessing = false;
            if (progress.errorMessage) {
              const isTokenError = progress.errorMessage.toLowerCase().includes('authorization') ||
                                   progress.errorMessage.toLowerCase().includes('reauthorize');
              if (isTokenError) {
                this.snackBar.open('Xero authorization expired. Please reauthorize to continue.', 'Close', { duration: 8000 });
                this.openXeroReauthorizeModal();
              } else {
                this.snackBar.open(`Invoices failed: ${progress.errorMessage}`, 'Close', { duration: 10000 });
              }
            } else {
              const msg = progress.skipped > 0
                ? `Invoices completed: ${progress.sent} sent, ${progress.skipped} skipped`
                : `Invoices completed: ${progress.sent} invoice${progress.sent !== 1 ? 's' : ''} sent successfully`;
              this.snackBar.open(msg, 'Close', { duration: 8000 });
            }
          }
        },
        error: () => { /* ignore transient polling errors */ }
      });
    }, 3000);
  }

  private stopInvoicePolling(): void {
    if (this.invoicePollInterval !== null) {
      clearInterval(this.invoicePollInterval);
      this.invoicePollInterval = null;
    }
  }

  private openXeroReauthorizeModal(): void {
    this.dialog.open(XeroReauthorizeModalComponent, {
      width: '500px',
      disableClose: true,
      panelClass: 'xero-reauthorize-dialog'
    });
  }

  previewAlerts(): void {
    this.loadingAlertsPreview = true;
    this.alertsDataSource.sort = null;
    this.http.get(`charitycentral/GetAlertsPreview?UserId=${this.userId}`).subscribe({
      next: (data: any) => {
        this.alertsDataSource.data = data;
        this.showAlertsPreview = true;
        this.loadingAlertsPreview = false;
        if (data.length === 0) {
          this.snackBar.open('No alerts to preview', 'Close', { duration: 5000 });
        }
      },
      error: (error) => {
        this.loadingAlertsPreview = false;
        this.snackBar.open(`Error loading alerts preview: ${error.message}`, 'Close', { duration: 5000 });
        console.error('Error:', error);
      }
    });
  }

  previewInvoices(): void {
    this.loadingInvoicesPreview = true;
    this.selectedInvoiceIds = [];
    this.invoicesDataSource.sort = null;

    this.http.get(`charitycentral/GetInvoicesPreview?UserId=${this.userId}`).subscribe({
      next: (data: any) => {
        this.invoicesDataSource.data = data;
        this.showInvoicesPreview = true;
        this.loadingInvoicesPreview = false;
        if (data.length === 0) {
          this.snackBar.open('No invoices to preview', 'Close', { duration: 5000 });
        }
      },
      error: (error) => {
        this.loadingInvoicesPreview = false;
        this.snackBar.open(`Error loading invoices preview: ${error.message}`, 'Close', { duration: 5000 });
        console.error('Error:', error);
      }
    });
  }
}
